import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { BaseEditCopyService, HttpService, NotificationService } from '../../../../tms-shared';
import {
  BillOfMaterialsItem,
  CustomerPrices,
  Product,
  ProductCost,
  ProductDeliveryItem,
  ProductDetails,
  ProductExtraItem,
  ProductForSalesOrderLine,
  ProductProductType,
  ProductsForCreatePurchaseOrder,
  ProductSpecPrint,
  ProductUsedIn,
  PurchaseOrderLineProduct,
  PurchaseOrderMultiProductCreate,
  SalesOrderMultiProductCreate,
  StockForecasting,
} from '../../../models';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends BaseEditCopyService<Product, ProductDetails> {
  public refreshDetails = new Subject<void>();

  constructor(
    protected httpService: HttpService,
    protected notificationService: NotificationService,
  ) {
    super('Product', $localize`Product`, httpService, notificationService);
  }

  getProductForSalesOrder(
    id: string,
    customerId: string,
    searchText: string,
    salesOrderHeaderId: string,
  ): Observable<ProductForSalesOrderLine> {
    return this.httpService.get<ProductForSalesOrderLine>(
      `${this.apiUrl}/GetProductForSalesOrder?productId=${id}&customerId=${customerId}&productSearchText=${searchText}&salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getProductForPurchaseOrder(id: string, supplierId: string): Observable<PurchaseOrderLineProduct> {
    return this.httpService.get<PurchaseOrderLineProduct>(
      `${this.apiUrl}/GetProductForPurchaseOrder?productId=${id}&supplierId=${supplierId}`,
    );
  }

  getForBillOfMaterials(search: string, productId: string): Observable<BillOfMaterialsItem[]> {
    return this.httpService.get<BillOfMaterialsItem[]>(
      `${this.apiUrl}/GetProductsForBillOfMaterials?search=${search}&productId=${productId}`,
    );
  }

  getForSalesOrderMultiCreate(search: string, salesOrderHeaderId: string): Observable<SalesOrderMultiProductCreate[]> {
    return this.httpService.get<SalesOrderMultiProductCreate[]>(
      `${this.apiUrl}/GetProductsForSalesOrderMultiCreate?search=${search}&salesOrderHeaderId=${salesOrderHeaderId}`,
    );
  }

  getForPurchaseOrderMultiCreate(
    search: string,
    purchaseOrderHeaderId: string,
  ): Observable<PurchaseOrderMultiProductCreate[]> {
    return this.httpService.get<PurchaseOrderMultiProductCreate[]>(
      `${this.apiUrl}/GetProductsForPurchaseOrderMultiCreate?search=${search}&purchaseOrderHeaderId=${purchaseOrderHeaderId}`,
    );
  }

  getProductsUsedIn(productId: string): Observable<ProductUsedIn[]> {
    return this.httpService.get<ProductUsedIn[]>(`${this.apiUrl}/GetProductsUsedIn?productId=${productId}`);
  }

  getDeliveryProduct(
    productId: string,
    countryId: string,
    postcode: string,
    chargeDelivery: boolean,
  ): Observable<ProductDeliveryItem> {
    return this.httpService.get<ProductDeliveryItem>(
      `${this.apiUrl}/GetDeliveryProduct?productId=${productId}&countryId=${countryId}&postcode=${postcode}&chargeDelivery=${chargeDelivery}`,
    );
  }

  getExtraProduct(productId: string): Observable<ProductExtraItem> {
    return this.httpService.get<ProductExtraItem>(`${this.apiUrl}/GetExtraProduct?productId=${productId}`);
  }

  checkKey(key: string, id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckKey?key=${key}&id=${id ?? ''}`);
  }

  checkStockExists(id: string, checked: boolean): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckStockExists?id=${id}&checkedValue=${checked}`);
  }

  getProductsForCreatePurchaseOrder(search: string): Observable<ProductsForCreatePurchaseOrder[]> {
    return this.httpService.get<ProductsForCreatePurchaseOrder[]>(
      `${this.apiUrl}/GetProductsForCreatePurchaseOrder?search=${search}`,
    );
  }

  getDefaultProductTypeDetails(productTypeId: string): Observable<ProductProductType> {
    return this.httpService.get<ProductProductType>(`${this.apiUrl}/GetProductTypeDefaultDetails/${productTypeId}`);
  }

  getProductCostBreakdown(productId: string): Observable<ProductCost> {
    return this.httpService.get<ProductCost>(`${this.apiUrl}/ProductCostBreakdown?productId=${productId}`);
  }

  getProductByKey(productKey: string): Observable<Product> {
    return this.httpService.get<Product>(`${this.apiUrl}/ProductByKey?productKey=${productKey}`);
  }

  getProductForSpecPrint(productId: string): Observable<ProductSpecPrint> {
    return this.httpService.get<ProductSpecPrint>(`${this.apiUrl}/GetProductForSpecPrint?productId=${productId}`);
  }

  getStockForecasting(
    search: string,
    productId: string,
    monthsToAnalyse: number,
    showAllDetails: boolean,
    minimumStockCalculation: string,
    ignorePurchaseOrders: boolean,
    monthToStart: string,
  ): Observable<StockForecasting[]> {
    return this.httpService.get<StockForecasting[]>(
      `${this.apiUrl}/StockForecasting?search=${search}&productId=${productId}&monthsToAnalyse=${monthsToAnalyse}&showAllDetails=${showAllDetails}&minimumStockCalculation=${minimumStockCalculation}&ignorePurchaseOrders=${ignorePurchaseOrders}&monthToStart=${monthToStart}`,
    );
  }

  getCustomerPrices(productId: string): Observable<CustomerPrices[]> {
    return this.httpService.get<CustomerPrices[]>(`${this.apiUrl}/CustomerPrices?productId=${productId}`);
  }

  updateIgnoreOnStockForecasting(id: string): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/UpdateIgnoreOnStockForecasting`, { id });
  }
}
